import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaTimes } from "react-icons/fa";
import { createSubTask } from "./subTaskService";
import { fetchTaskStatuses, fetchProjectUsers } from "./taskService";
import { extractCreatedSubTaskId, uploadSubTaskFile } from "./taskFileService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";
import "../assets/css/Permissions.css";
import "../assets/css/Issues.css";

function AddSubTask({ onBack, onSubTaskCreated }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId, taskId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canAddSubTaskFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [error, setError] = useState("");
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAssigneesDropdown, setShowAssigneesDropdown] = useState(false);
  const TASK_PRIORITIES = ["High", "Medium", "Low"];

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    statusId: "",
    priority: "Medium",
    assignedUserIds: []
  });

  useEffect(() => {
    const load = async () => {
      try {
        const statuses = await fetchTaskStatuses();
        const sorted = (Array.isArray(statuses) ? statuses : []).sort(
          (a, b) => (a.Status_Id ?? a.status_id ?? 0) - (b.Status_Id ?? b.status_id ?? 0)
        );
        setTaskStatuses(sorted);
        if (sorted.length > 0) {
          const defaultId = sorted[0]?.Status_Id ?? sorted[0]?.status_id;
          if (defaultId != null) {
            setFormData((prev) => (prev.statusId ? prev : { ...prev, statusId: String(defaultId) }));
          }
        }
      } catch (err) {
        console.error("Error fetching task statuses:", err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const projectUsers = await fetchProjectUsers(projectId, hasSubproject ? subprojectId : null);
        const mapped = projectUsers.map((u) => ({
          id: u.User_Id ?? u.user_id ?? u.id,
          name: (u.User_Name ?? u.user_name) || "—"
        })).filter((x) => x.id);
        setUsers(mapped);
      } catch (err) {
        console.error("Error fetching project users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [projectId, subprojectId, hasSubproject]);

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.assignedUserIds.includes(userId) ? prev.assignedUserIds.filter((id) => id !== userId) : [...prev.assignedUserIds, userId];
      return { ...prev, assignedUserIds: ids };
    });
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.assignedUserIds.length === users.length;
  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: allUsersSelected ? [] : users.map((u) => u.id)
    }));
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (showAssigneesDropdown && !e.target.closest(".form-group-multi-select")) {
        setShowAssigneesDropdown(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [showAssigneesDropdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    const todayStr = new Date().toISOString().split("T")[0];
    if (formData.startDate && formData.startDate < todayStr) {
      setError("Start date cannot be in the past.");
      return;
    }
    if (formData.endDate && formData.endDate < todayStr) {
      setError("End date cannot be in the past.");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setError("");
    let createdBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      Task_Id: Number(taskId),
      Story_Id: Number(userStoryId),
      Title: title,
      Description: (formData.description || "").trim(),
      ...(formData.statusId ? { Status_Id: Number(formData.statusId) } : {}),
      Priority: formData.priority || "Medium",
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Created_By: createdBy,
      Assigned_User_Ids: (formData.assignedUserIds || []).map((id) => Number(id))
    };

    setSubmitting(true);
    try {
      const created = await createSubTask(payload);
      const newId =
        extractCreatedSubTaskId(created) ??
        (() => {
          const raw = created?.Sub_Task_Id ?? created?.sub_task_id ?? created?.id;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : null;
        })();

      if (pendingFiles.length > 0) {
        if (newId == null) {
          alert(
            "Subtask was created, but the response did not include a subtask id, so attachments were not uploaded. Add them from View or Edit subtask."
          );
          setPendingFiles([]);
        } else {
          for (const file of pendingFiles) {
            try {
              await uploadSubTaskFile(newId, file);
            } catch (uploadErr) {
              console.error("Subtask file upload after create:", uploadErr);
              alert(
                uploadErr.response?.data?.message ||
                  uploadErr.response?.data?.error ||
                  "Subtask created, but a file failed to upload. Add files from the subtask page."
              );
              break;
            }
          }
          setPendingFiles([]);
        }
      }

      alert("Subtask created successfully.");
      if (onSubTaskCreated) onSubTaskCreated();
      if (onBack) onBack();
      else if (hasSubproject) {
        navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
      } else {
        navigate(`/projects/${projectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
      }
    } catch (err) {
      console.error("Error creating subtask:", err);
      setError(
        err.response?.data?.message || err.response?.data?.error || "Failed to create subtask."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  const goBack = () => {
    if (onBack) onBack();
    else if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
    }
  };

  return (
    <div className="project-form-container editcustomer-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <form className="editcustomer-form edituserstory-form" onSubmit={handleSubmit}>
        <div className="form-section">
          {error && (
            <div className="userstories-add-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="form-grid addtask-form-grid">
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Subtask title"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="statusId"
                value={formData.statusId}
                onChange={handleInputChange}
              >
                {taskStatuses.length === 0 && (
                  <option value="">Backlog</option>
                )}
                {taskStatuses.map((s) => (
                  <option key={s.Status_Id ?? s.status_id} value={String(s.Status_Id ?? s.status_id)}>
                    {s.Status ?? s.status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                min={today}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                min={formData.startDate || today}
              />
            </div>
            <div className="form-group form-group-multi-select">
              <label>Assigned Users</label>
              <div style={{ width: "100%" }}>
                <div
                  className="permissions-select-trigger"
                  onClick={() => {
                    if (!loadingUsers) setShowAssigneesDropdown((prev) => !prev);
                  }}
                  style={{
                    opacity: loadingUsers ? 0.6 : 1,
                    cursor: loadingUsers ? "default" : "pointer"
                  }}
                >
                  <span className="permissions-select-text">
                    {loadingUsers
                      ? "Loading users..."
                      : formData.assignedUserIds.length === 0
                        ? "Select assignees"
                        : `${formData.assignedUserIds.length} user${formData.assignedUserIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showAssigneesDropdown ? "open" : ""}`}
                  />
                </div>
                {showAssigneesDropdown && !loadingUsers && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allUsersSelected}
                        onChange={handleToggleSelectAllUsers}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>
                    {users.map((u) => (
                      <label key={u.id} className="permissions-select-item">
                        <input
                          type="checkbox"
                          checked={formData.assignedUserIds.includes(u.id)}
                          onChange={() => handleUserToggle(u.id)}
                        />
                        <span className="permissions-select-item-label">{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group addtask-description-full">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Subtask description"
              />
            </div>
            {canAddSubTaskFiles ? (
              <div className="form-group addtask-description-full">
                <label>Attachments (optional)</label>
                <input
                  type="file"
                  className="issue-files-native-input"
                  disabled={submitting}
                  multiple
                  onChange={(e) => {
                    const next = Array.from(e.target.files || []);
                    setPendingFiles((prev) => [...prev, ...next]);
                    e.target.value = "";
                  }}
                />
                {pendingFiles.length > 0 ? (
                  <ul className="issue-files-pending-list">
                    {pendingFiles.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="issue-files-pending-item">
                        <span className="issue-files-pending-name">{f.name}</span>
                        <button
                          type="button"
                          className="issue-files-pending-remove"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                          disabled={submitting}
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="form-actions editcustomer-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Creating..." : "Create Subtask"}
          </button>
          <button type="button" className="btn-cancel" onClick={goBack} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddSubTask;
